import React, { useState, useEffect, useRef } from "react";

interface NavItem {
  id: string;
  label: string;
  labelShort?: string;
  active: boolean;
}

const ScrollNav: React.FC = () => {
  const [navItems, setNavItems] = useState<NavItem[]>([
    {
      id: "technology",
      label: "Technology",
      labelShort: "Technology",
      active: true,
    },
    {
      id: "future-battery-architectures",
      label: "Future Battery Architecture",
      labelShort: "Future",
      active: false,
    },
    {
      id: "academic-cooperation",
      label: "Collaboration",
      labelShort: "Collaborate",
      active: false,
    },
  ]);

  const [isHovered, setIsHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sectionsRef = useRef<Map<string, number>>(new Map());
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const elementRect = element.getBoundingClientRect();
      const absoluteElementTop = window.scrollY + elementRect.top;
      const headerOffset = 80;

      window.scrollTo({
        top: absoluteElementTop - headerOffset,
        behavior: "smooth",
      });

      setNavItems((prev) =>
        prev.map((item) => ({
          ...item,
          active: item.id === id,
        }))
      );

      if (isMobile) {
        setIsHovered(false);
      }
    }
  };

  useEffect(() => {
    navItems.forEach((item) => {
      const element = document.getElementById(item.id);
      if (element) {
        sectionsRef.current.set(item.id, 0);
      }
    });

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.id;
          sectionsRef.current.set(id, entry.intersectionRatio);
        });

        let maxVisibility = 0;
        let mostVisibleSection = navItems[0].id;

        sectionsRef.current.forEach((visibility, id) => {
          if (visibility > maxVisibility) {
            maxVisibility = visibility;
            mostVisibleSection = id;
          }
        });

        if (maxVisibility > 0.1) {
          setNavItems((prev) =>
            prev.map((item) => ({
              ...item,
              active: item.id === mostVisibleSection,
            }))
          );
        }
      },
      {
        threshold: [0, 0.1, 0.3, 0.5, 0.7, 1],
        rootMargin: "-100px 0px -100px 0px",
      }
    );

    navItems.forEach((item) => {
      const element = document.getElementById(item.id);
      if (element) {
        observerRef.current?.observe(element);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [navItems]);

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  if (!isMobile) {
    return (
      <div className="fixed left-0 top-1/2 transform -translate-y-1/2 z-50">
        <div className="p-2">
          <div className="space-y-4">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`
                  block text-left px-4 py-2 transition-all duration-300 
                  w-48
                  ${
                    item.active
                      ? "border-l-4 border-blue-600 text-blue-600 font-medium"
                      : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                  }
                `}
              >
                <div className="flex items-center">
                  {item.id === "future-battery-architectures" ? (
                    <div className="flex flex-col items-start">
                      <span className="text-sm font-light leading-tight">
                        Future Battery
                      </span>
                      <span className="text-sm font-light leading-tight">
                        Architecture
                      </span>
                    </div>
                  ) : (
                    <span className="text-sm font-light whitespace-nowrap">
                      {item.label}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <button
        className="fixed left-0 top-1/2 transform -translate-y-1/2 z-50 bg-white shadow-lg rounded-r-lg transition-all duration-300 hover:shadow-xl"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={() => setIsHovered(!isHovered)}
        aria-label="Open navigation menu"
      >
        <div className="p-2 flex items-center justify-center">
          <svg
            className={`w-6 h-6 text-gray-700 transition-transform duration-300 ${
              isHovered ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d={isHovered ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"}
            />
          </svg>
        </div>
      </button>

      <div
        className={`fixed left-0 top-1/2 transform -translate-y-1/2 z-40 transition-all duration-300 ${
          isHovered
            ? "translate-x-0 opacity-100"
            : "-translate-x-full opacity-0"
        }`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="p-2 pl-10 bg-white/95 backdrop-blur-sm shadow-xl rounded-r-lg">
          <div className="space-y-3">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`
                  block text-left px-3 py-2 transition-all duration-200 
                  w-40
                  ${
                    item.active
                      ? "border-l-4 border-blue-600 text-blue-600 font-medium bg-blue-50"
                      : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                  }
                `}
              >
                <div className="flex items-center">
                  {item.id === "future-battery-architectures" ? (
                    <div className="flex flex-col items-start">
                      <span className="text-xs font-light leading-tight">
                        Future Battery
                      </span>
                      <span className="text-xs font-light leading-tight">
                        Architecture
                      </span>
                    </div>
                  ) : (
                    <span className="text-xs font-light whitespace-nowrap">
                      {item.labelShort || item.label}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default ScrollNav;
